import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import LibraryCard from '../Library/LibraryCard';
import { deleteEdit, duplicateEdit, fetchEdits, saveEdit, fetchEdit, type EditSummary } from '../../services/editingApi';
import { formatMs } from './editStudioModel';

interface QuickEditsLibraryProps {
  /** A frame for each edit's card: its video's (or its set's first angle's) file. */
  posterFor: (edit: EditSummary) => string | null;
  /** Filters by title (the Library's search box). */
  searchTerm: string;
  /** Opens the videos tab, where "Quick edit" starts one. */
  onShowVideos: () => void;
  onCount?: (count: number) => void;
}

const EXPORT_LABEL: Record<string, string> = { done: 'Exported', failed: 'Export didn’t work', cancelled: 'Export cancelled', expired: 'Export expired', queued: 'Exporting', preparing: 'Exporting', rendering: 'Exporting', uploading: 'Exporting' };

const relative = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 3600 * 1000));
  if (days <= 0) return 'Changed today';
  if (days === 1) return 'Changed yesterday';
  if (days < 30) return `Changed ${days} days ago`;
  return `Changed ${date.toLocaleDateString()}`;
};

/** Every quick edit of the account: open, rename, copy, delete. */
const QuickEditsLibrary: React.FC<QuickEditsLibraryProps> = ({ posterFor, searchTerm, onShowVideos, onCount }) => {
  const [edits, setEdits] = useState<EditSummary[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<EditSummary | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const load = useCallback(async () => {
    try {
      const list = await fetchEdits();
      setEdits(list);
      onCount?.(list.length);
    } catch (error) {
      setEdits([]);
      setProblem(error instanceof Error ? error.message : 'Your quick edits couldn’t be loaded.');
    }
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const copy = async (edit: EditSummary) => {
    setProblem(null);
    try {
      await duplicateEdit(edit.id);
      await load();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be copied.');
    }
  };
  const remove = async (edit: EditSummary) => {
    if (!window.confirm(`Delete “${edit.title}”? Its exports stay until they expire; the edit can’t be brought back.`)) return;
    setProblem(null);
    try {
      await deleteEdit(edit.id);
      await load();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be deleted.');
    }
  };
  const rename = async () => {
    if (!renaming) return;
    const target = renaming;
    const title = newTitle.trim();
    setRenaming(null);
    if (!title || title === target.title) return;
    try {
      const full = await fetchEdit(target.id);
      if (!full) return;
      await saveEdit(full.id, full.version, full.list, title);
      await load();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The name didn’t save.');
    }
  };

  if (edits === null) return <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  const term = searchTerm.trim().toLowerCase();
  const visible = term ? edits.filter((edit) => edit.title.toLowerCase().includes(term)) : edits;

  return (
    <Box data-testid="quick-edits-library">
      {problem ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProblem(null)}>{problem}</Alert> : null}
      {edits.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <AutoFixHighOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>Make your first quick edit</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 520, mx: 'auto' }}>
            Trim a video, add your logo, captions and music, reframe it for Shorts and export it yourself, without waiting for an editor. Every video has a Quick edit button.
          </Typography>
          <Button variant="contained" onClick={onShowVideos}>Choose a video</Button>
        </Box>
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No quick edit matches “{searchTerm}”.</Typography>
      ) : (
        <Grid container spacing={2.5}>
          {visible.map((edit) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={edit.id}>
              <LibraryCard
                title={edit.title}
                meta={[formatMs(edit.outputDurationMs), edit.summary, relative(edit.updatedAt)]}
                posterSrc={posterFor(edit)}
                status={edit.lastExport ? <Chip size="small" label={EXPORT_LABEL[edit.lastExport.status] || 'Exported'} color={edit.lastExport.status === 'done' ? 'success' : edit.lastExport.status === 'failed' ? 'error' : 'default'} variant="outlined" /> : undefined}
                openLabel="Open"
                openTo={`/edit/${edit.id}`}
                actions={[
                  { label: 'Rename', icon: <EditOutlinedIcon fontSize="small" />, onClick: () => { setRenaming(edit); setNewTitle(edit.title); }, testId: `rename-edit-${edit.id}` },
                  { label: 'Make a copy', icon: <ContentCopyOutlinedIcon fontSize="small" />, onClick: () => copy(edit), testId: `copy-edit-${edit.id}` },
                  { label: 'Delete', icon: <DeleteOutlineOutlinedIcon fontSize="small" />, onClick: () => remove(edit), destructive: true, testId: `delete-edit-${edit.id}` },
                ]}
                testId={`edit-card-${edit.id}`}
                openTestId={`open-edit-${edit.id}`}
                menuTestId={`edit-actions-${edit.id}`}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog open={Boolean(renaming)} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename the edit</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" value={newTitle} onChange={(event) => setNewTitle(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') rename(); }} inputProps={{ maxLength: 120, 'aria-label': 'Name' }} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button variant="contained" onClick={rename} disabled={!newTitle.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickEditsLibrary;
